import { useState } from "react";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { DecisionOutcome, decideChangeRequest } from "../../../../utils/icGovernance";
import ActionError from "../detail/ActionError";
import FieldGrid from "../detail/FieldGrid";
import SideCard from "../detail/SideCard";
import { DetailOf, GovSettingsOrNull, ItemActionProps } from "../detail/types";
import {
  FAINT,
  MUTED,
  approveButtonSx,
  neutralButtonSx,
  rejectButtonSx,
  todayLabel,
} from "../detail/ui";
import { useGovAction } from "../detail/useGovAction";
import {
  DecisionChip,
  SignOffChip,
  clientLabel,
  dateOnly,
  hours,
  shortDate,
  statusLabel,
  vendorLabel,
} from "../governanceStatus";
import ReleaseLink from "./ReleaseLink";

type CrProps = { item: DetailOf<"CHANGE_REQUEST"> } & ItemActionProps;

/** Approve / Reject (needs a comment) / Defer — shown when `permissions.decide`. */
const DecisionForm = ({ item, onUpdated, onReload }: CrProps) => {
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState<DecisionOutcome | null>(null);
  const action = useGovAction();

  const decide = async (outcome: DecisionOutcome) => {
    const text = comment.trim();
    if (outcome === "REJECTED" && !text) return;
    setPending(outcome);
    const detail = await action.run(() => decideChangeRequest(item.id, outcome, text || undefined));
    setPending(null);
    if (detail) {
      setComment("");
      onUpdated(detail, { text: `Decision recorded: ${statusLabel(outcome)} · ${todayLabel()}` });
    }
  };

  const spinner = (outcome: DecisionOutcome) =>
    pending === outcome ? <CircularProgress size={16} color="inherit" /> : null;

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        label="Comment (required to reject)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={action.busy}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          color="success"
          disableElevation
          onClick={() => decide("APPROVED")}
          disabled={action.busy}
          startIcon={spinner("APPROVED")}
          sx={{ ...approveButtonSx, minWidth: 130 }}
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => decide("REJECTED")}
          disabled={action.busy || !comment.trim()}
          startIcon={spinner("REJECTED")}
          sx={{ ...rejectButtonSx, minWidth: 130 }}
        >
          Reject
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => decide("DEFERRED")}
          disabled={action.busy}
          startIcon={spinner("DEFERRED")}
          sx={{ ...neutralButtonSx, minWidth: 130 }}
        >
          Defer
        </Button>
      </Stack>
      <ActionError action={action} onReload={onReload} />
    </Box>
  );
};

/** Request · impact assessment · decision · delivery. */
const ChangeRequestPanel = ({ item, settings, onUpdated, onReload }: CrProps & { settings: GovSettingsOrNull }) => {
  const client = clientLabel(settings);
  const vendor = vendorLabel(settings);
  const { decision, signOff } = item;

  return (
    <Stack spacing={2}>
      <SideCard title="Request">
        <FieldGrid
          fields={[
            { label: "Requested by", value: item.requestedBy },
            {
              label: "Date requested",
              // requestedAt is a date field; fall back to the creation timestamp.
              value: item.requestedAt ? dateOnly(item.requestedAt) : shortDate(item.createdAt),
            },
            { label: "Detailed request", value: item.detailedRequest, full: true },
            { label: "Business reason", value: item.businessReason, full: true },
          ]}
        />
      </SideCard>

      <SideCard title={`${vendor} impact assessment`}>
        <FieldGrid
          fields={[
            { label: "Estimated hours", value: hours(item.estimatedMinutes) },
            { label: "Schedule impact", value: item.scheduleImpact },
            { label: "Impact assessment", value: item.impactAssessment || "Not yet written", full: true },
          ]}
        />
      </SideCard>

      <SideCard title={`${client} decision`} action={<DecisionChip outcome={decision?.outcome} />}>
        {decision?.outcome ? (
          <FieldGrid
            fields={[
              { label: "Outcome", value: statusLabel(decision.outcome) },
              { label: "Decided by", value: decision.by },
              { label: "Date", value: shortDate(decision.at) },
              { label: "Comment", value: decision.comment, full: true },
            ]}
          />
        ) : !item.permissions?.decide ? (
          <Typography variant="body2" sx={{ color: FAINT }}>
            No decision yet.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: MUTED }}>
            This change request is waiting for your decision.
          </Typography>
        )}
        {item.permissions?.decide ? <DecisionForm item={item} onUpdated={onUpdated} onReload={onReload} /> : null}
      </SideCard>

      <SideCard title="Delivery">
        <FieldGrid
          fields={[
            {
              label: "Target release",
              value: <ReleaseLink release={item.targetRelease} empty="Not scheduled yet" />,
              full: true,
            },
            {
              label: `${client} sign-off`,
              value: (
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
                  <SignOffChip status={signOff?.status} />
                  {signOff?.decidedBy && (signOff.status === "APPROVED" || signOff.status === "REJECTED") ? (
                    <Typography component="span" variant="body2" sx={{ color: MUTED }}>
                      by {signOff.decidedBy} · {shortDate(signOff.decidedAt)}
                    </Typography>
                  ) : null}
                </Stack>
              ),
              full: true,
            },
          ]}
        />
      </SideCard>
    </Stack>
  );
};

export default ChangeRequestPanel;
